import {
  Button,
  Flex,
  Popover,
  PopoverHeader,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';

import Pencil from 'components/Icons/Pencil';
import Plus from 'components/Icons/Plus';
import { getCalculationLoadingStatus } from 'redux/DashboardSliceV2';
import { STATUS } from 'projectConstants';

import IndicatorMultiSelector from './IndicatorMultiSelector';

export interface Properties {
  selectedIndicators: Array<any>;
  handleSubmit: (indicators: Array<any>) => void;
}

const SelectionPopover = ({ selectedIndicators, handleSubmit }: Properties) => {
  const [localIndicators, setLocalIndicators] = useState<Array<any>>([]);
  const isLoading = useSelector(getCalculationLoadingStatus);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (selectedIndicators.length > 0) {
      setLocalIndicators(selectedIndicators);
    }
  }, [selectedIndicators]);

  const handleAddIndicator = (indicator: any) => {
    const isSelected = localIndicators.some(
      (selected: any) => selected.indicator_id === indicator.indicator_id,
    );
    const wasPreviouslySelected = selectedIndicators.some(
      (selected: any) => selected.indicator_id === indicator.indicator_id,
    );
    let updatedIndicators = [...localIndicators];
    if (wasPreviouslySelected) {
      const previouslySelectedIndicatorIndex = selectedIndicators.findIndex(
        (selected: any) => selected.indicator_id === indicator.indicator_id,
      );
      if (previouslySelectedIndicatorIndex > -1) updatedIndicators = [...localIndicators];
      updatedIndicators.splice(
        previouslySelectedIndicatorIndex,
        0,
        selectedIndicators[previouslySelectedIndicatorIndex],
      );
    } else if (isSelected) {
      updatedIndicators = updatedIndicators.filter(
        (selected: any) => selected.indicator_id !== indicator.indicator_id,
      );
    } else {
      updatedIndicators.push(indicator);
    }
    setLocalIndicators(updatedIndicators);
  };
  const handleRemoveIndicator = (indicator: any) => {
    const updatedIndicators = localIndicators.filter(
      (selected: any) => selected.indicator_id !== indicator.indicator_id,
    );
    setLocalIndicators(updatedIndicators);
  };

  return (
    <Popover
      strategy={'fixed'}
      placement='left'
      direction='ltr'
      closeOnBlur={false}
      isOpen={isOpen}
    >
      <PopoverTrigger>
        <Button width={'14.583px'} onClick={() => setIsOpen(!isOpen)} variant='transparent'>
          {selectedIndicators?.length ? (
            <Pencil width={3} height={3} color='#708090' />
          ) : (
            <Plus color='#708090' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        bg='white'
        border='1px solid #D9D9D9'
        borderRadius='14px'
        boxShadow='0px 8px 24px rgba(18, 24, 38, 0.16)'
        p='16px'
        maxW='380px'
      >
        <PopoverHeader
          border='none'
          p={0}
          mb='14px'
          fontFamily='Comfortaa'
          fontWeight='bold'
          color='#121826'
          fontSize='16px'
        >
          Select indicator
        </PopoverHeader>
        <PopoverBody p={0}>
          <IndicatorMultiSelector
            selectedIndicators={localIndicators}
            handleAddIndicator={handleAddIndicator}
            handleRemoveIndicator={handleRemoveIndicator}
          />
          <Flex justifyContent='flex-end' alignItems='center' mt='14px' gap='11px'>
            <Button
              size='sm'
              h='30px'
              px='12px'
              bg='white'
              border='1px solid #D9D9D9'
              borderRadius='8px'
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='12px'
              color='#121826'
              _hover={{ bg: 'gray.50' }}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              h='30px'
              px='12px'
              bg='#8B5CF6'
              borderRadius='8px'
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='12px'
              color='white'
              _hover={{ bg: '#7C4DEF' }}
              onClick={() => {
                setTimeout(() => {
                  if (isEqual(selectedIndicators, localIndicators)) {
                    setIsOpen(false);
                  } else {
                    handleSubmit(localIndicators);
                    setIsOpen(false);
                  }
                }, 1000);
              }}
              disabled={
                !(isLoading === STATUS.SUCCESS || isLoading === STATUS.IDLE) ||
                !localIndicators ||
                !localIndicators?.length
              }
            >
              Apply
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
export default SelectionPopover;
